import type { Mixpanel } from "lib/mixpanel/mixpanel";
import type { UserProfile } from "lib/store/user";
import { AuthType } from "../types";
import type { PaywallVariant, PaywallWindow } from "lib/types/paywalls";

type AuthData = Record<string, string | boolean>;

const TRACKER_KEY = "auth-tracker";

export class Tracker {
  private mixpanel: Mixpanel;

  constructor(mixpanel: Mixpanel) {
    this.mixpanel = mixpanel;
  }

  init(userProfile: UserProfile) {
    const { type: authType, data: authData } = JSON.parse(
      window.sessionStorage.getItem(TRACKER_KEY) ?? '{"data":{}}'
    );
    window.sessionStorage.removeItem(TRACKER_KEY);

    switch (authType) {
      case AuthType.LOGIN:
        return this.trackLogin(userProfile, authData);

      case AuthType.REGISTRATION:
        return this.trackRegistration(userProfile, authData);
    }
  }

  trackAuth(type: AuthType, data: AuthData) {
    data.originUrl = window.location.href;

    window.sessionStorage.setItem(TRACKER_KEY, JSON.stringify({ type, data }));
  }

  trackPaywall(
    userProfile: UserProfile,
    paywall: PaywallWindow,
    variant: PaywallVariant = "modal"
  ) {
    this.mixpanel.track("Barrier show", {
      distinct_id: userProfile.trackingId,
      "Barrier id": paywall.id,
      "Barrier name": paywall.name,
      "Barrier variant": variant,
    });
  }

  trackPaywallButton(
    userProfile: UserProfile,
    paywall: PaywallWindow,
    ctaLabel: string | null | undefined,
    variant: PaywallVariant = "modal"
  ) {
    this.mixpanel.track("PaywallButtonClick", {
      distinct_id: userProfile.trackingId,
      "Paywall ID": paywall.id,
      "CTA Label": ctaLabel,
      "Paywall variant": variant,
    });
  }

  trackLogout(userProfile: UserProfile) {
    this.mixpanel.track("Logout", {
      distinct_id: userProfile.trackingId,
      "Page Path": window.location.href,
    });
  }

  private trackLogin(userProfile: UserProfile, authData: AuthData) {
    this.mixpanel.identify(userProfile.trackingId);

    this.mixpanel.track("Login", {
      "Login type": "email",
      Flow: "general",
      distinct_id: userProfile.trackingId,
      "Origin URL": authData.originUrl,
    });

    this.mixpanel.peopleSet({
      $name: `${userProfile.first_name} ${userProfile.last_name}`,
      $first_name: userProfile.first_name,
      $last_name: userProfile.last_name,
      $email: authData.email,
      ...(userProfile.membership ? { membership: userProfile.membership } : {}),
      ...userProfile.details,
    });
  }

  private trackRegistration(userProfile: UserProfile, authData: AuthData) {
    this.mixpanel.identify(userProfile.trackingId);

    this.mixpanel.track("Registration", {
      "Registration type": "email",
      Flow: "general",
      distinct_id: userProfile.trackingId,
      "Origin URL": authData.originUrl,
    });

    this.mixpanel.peopleSet({
      $name: `${userProfile.first_name} ${userProfile.last_name}`,
      $first_name: userProfile.first_name,
      $last_name: userProfile.last_name,
      $email: authData.email,
      ...(userProfile.membership ? { membership: userProfile.membership } : {}),
      ...userProfile.details,
    });

    const selectedNewsletters = Object.entries(authData)
      .filter(([key, value]) => key.endsWith("-newsletter") && value)
      .map(([key]) => key);

    for (const newsletter of selectedNewsletters) {
      this.mixpanel.track("Profile newsletters", {
        "Registration type": "email",
        Flow: "general",
        distinct_id: userProfile.trackingId,
        "Origin URL": authData.originUrl,
        Newsletter: newsletter,
      });
    }
  }
}
